import { apiClient } from './client';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import type { LessonContentKind, LessonLink, QuizOption } from '@/api/training.api';
import type { SystemRole } from '@/types';

/**
 * Client for the training AUTHOR/REPORTING surfaces (ADR-025):
 *
 *   - `/api/training/courses/*`     — course/lesson/quiz authoring
 *                                     (manage_training)
 *   - `/api/training/assignments/*` — assignment rules (manage_training)
 *   - `/api/training/reports/*`     — completion matrix + overdue
 *                                     (view_training_reports)
 *   - `/api/training/checklists/*`  — onboarding checklist templates
 *                                     (manage_training) + per-hire
 *                                     instances (view_training_reports)
 *
 * Shapes mirror the route payloads 1:1 — keep in lockstep. Quiz shapes
 * here INCLUDE `correctOptionId` (author surface); the taker-facing
 * client in `training.api.ts` never sees it.
 */

// ── Courses ─────────────────────────────────────────────────────────────

export type CourseState = 'draft' | 'published' | 'archived';

export interface AdminCourse {
  id: string;
  organizationId: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  state: CourseState;
  version: number;
  publishedAt: string | null;
  passThresholdPercentDefault: number;
  createdByMembershipId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCourseListRow extends AdminCourse {
  lessonCount: number;
  quizCount: number;
}

export interface AdminQuizQuestion {
  id: string;
  quizId: string;
  order: number;
  prompt: string;
  optionsJson: QuizOption[];
  correctOptionId: string;
  explanation: string | null;
}

export interface AdminQuiz {
  id: string;
  organizationId: string;
  courseId: string;
  /** Null = the course final quiz; set = a per-lesson quiz. */
  lessonId: string | null;
  title: string;
  passThresholdPercent: number;
  maxAttempts: number | null;
  questions: AdminQuizQuestion[];
}

export interface AdminLesson {
  id: string;
  organizationId: string;
  courseId: string;
  title: string;
  order: number;
  contentKind: LessonContentKind;
  contentJson: TipTapDoc | null;
  sopId: string | null;
  mediaLinks: LessonLink[];
  attachmentLinks: LessonLink[];
  estimatedMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCourseDetail extends AdminCourse {
  lessons: (AdminLesson & { quiz: AdminQuiz | null })[];
  finalQuiz: AdminQuiz | null;
}

export async function listCourses(): Promise<AdminCourseListRow[]> {
  const res = await apiClient.get<{ data: AdminCourseListRow[] }>('/api/training/courses');
  return res.data.data;
}

export interface CreateCourseRequest {
  title: string;
  description?: string | null;
  category?: string | null;
  passThresholdPercentDefault?: number;
}

export async function createCourse(body: CreateCourseRequest): Promise<AdminCourse> {
  const res = await apiClient.post<{ data: AdminCourse }>('/api/training/courses', body);
  return res.data.data;
}

export async function getCourse(id: string): Promise<AdminCourseDetail> {
  const res = await apiClient.get<{ data: AdminCourseDetail }>(
    `/api/training/courses/${encodeURIComponent(id)}`,
  );
  return res.data.data;
}

export type UpdateCourseRequest = Partial<CreateCourseRequest> & { reasonForChange?: string };

export async function updateCourse(id: string, body: UpdateCourseRequest): Promise<AdminCourse> {
  const res = await apiClient.patch<{ data: AdminCourse }>(
    `/api/training/courses/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

export async function publishCourse(id: string, reasonForChange?: string): Promise<AdminCourse> {
  const res = await apiClient.post<{ data: AdminCourse }>(
    `/api/training/courses/${encodeURIComponent(id)}/publish`,
    reasonForChange ? { reasonForChange } : {},
  );
  return res.data.data;
}

export async function archiveCourse(id: string, reasonForChange?: string): Promise<AdminCourse> {
  const res = await apiClient.post<{ data: AdminCourse }>(
    `/api/training/courses/${encodeURIComponent(id)}/archive`,
    reasonForChange ? { reasonForChange } : {},
  );
  return res.data.data;
}

// ── Lessons ─────────────────────────────────────────────────────────────

export interface LessonFieldsRequest {
  title: string;
  contentKind: LessonContentKind;
  contentJson?: TipTapDoc;
  sopId?: string;
  mediaLinks?: LessonLink[];
  attachmentLinks?: LessonLink[];
  estimatedMinutes?: number | null;
  order?: number;
}

export async function addLesson(courseId: string, body: LessonFieldsRequest): Promise<AdminLesson> {
  const res = await apiClient.post<{ data: AdminLesson }>(
    `/api/training/courses/${encodeURIComponent(courseId)}/lessons`,
    body,
  );
  return res.data.data;
}

export type UpdateLessonRequest = Partial<LessonFieldsRequest> & { reasonForChange?: string };

export async function updateLesson(lessonId: string, body: UpdateLessonRequest): Promise<AdminLesson> {
  const res = await apiClient.patch<{ data: AdminLesson }>(
    `/api/training/courses/lessons/${encodeURIComponent(lessonId)}`,
    body,
  );
  return res.data.data;
}

/** Draft courses only — the server 409s (`course_published`) otherwise. */
export async function deleteLesson(lessonId: string): Promise<void> {
  await apiClient.delete(`/api/training/courses/lessons/${encodeURIComponent(lessonId)}`);
}

/** `orderedIds` must be a permutation of the course's lesson ids. */
export async function reorderLessons(courseId: string, orderedIds: string[]): Promise<AdminLesson[]> {
  const res = await apiClient.put<{ data: AdminLesson[] }>(
    `/api/training/courses/${encodeURIComponent(courseId)}/lessons/order`,
    { orderedIds },
  );
  return res.data.data;
}

// ── Quizzes (edited as documents; question list replaced wholesale) ─────

export interface QuizQuestionInput {
  prompt: string;
  options: QuizOption[];
  correctOptionId: string;
  explanation?: string | null;
}

export interface QuizDocumentInput {
  title: string;
  passThresholdPercent?: number;
  /** Null = unlimited attempts. */
  maxAttempts?: number | null;
  questions: QuizQuestionInput[];
  reasonForChange?: string;
}

export async function saveCourseFinalQuiz(courseId: string, doc: QuizDocumentInput): Promise<AdminQuiz> {
  const res = await apiClient.put<{ data: AdminQuiz }>(
    `/api/training/courses/${encodeURIComponent(courseId)}/quiz`,
    doc,
  );
  return res.data.data;
}

export async function saveLessonQuiz(lessonId: string, doc: QuizDocumentInput): Promise<AdminQuiz> {
  const res = await apiClient.put<{ data: AdminQuiz }>(
    `/api/training/courses/lessons/${encodeURIComponent(lessonId)}/quiz`,
    doc,
  );
  return res.data.data;
}

// ── Assignments ─────────────────────────────────────────────────────────

export type AssignmentTargetType = 'membership' | 'department' | 'role' | 'all';

export interface TrainingAssignment {
  id: string;
  organizationId: string;
  courseId: string;
  targetType: AssignmentTargetType;
  targetMembershipId: string | null;
  targetDepartmentId: string | null;
  targetRole: SystemRole | null;
  dueInDays: number | null;
  recertIntervalDays: number | null;
  renewalWindowDays: number;
  isActive: boolean;
  createdByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
  course: { id: string; slug: string; title: string; state?: CourseState };
  /** Present on the list payload only. */
  _count?: { enrollments: number };
}

export async function listAssignments(): Promise<TrainingAssignment[]> {
  const res = await apiClient.get<{ data: TrainingAssignment[] }>('/api/training/assignments');
  return res.data.data;
}

export interface CreateAssignmentRequest {
  courseId: string;
  targetType: AssignmentTargetType;
  targetMembershipId?: string;
  targetDepartmentId?: string;
  targetRole?: SystemRole;
  dueInDays?: number | null;
  recertIntervalDays?: number | null;
  renewalWindowDays?: number;
}

/** Creates AND materializes — the response reports how many people enrolled. */
export async function createAssignment(
  body: CreateAssignmentRequest,
): Promise<{ assignment: TrainingAssignment; enrollmentsCreated: number }> {
  const res = await apiClient.post<{
    data: { assignment: TrainingAssignment; enrollmentsCreated: number };
  }>('/api/training/assignments', body);
  return res.data.data;
}

/** Soft deactivate — existing enrollments stay (training history is append-only). */
export async function deactivateAssignment(id: string, reasonForChange: string): Promise<void> {
  await apiClient.delete(`/api/training/assignments/${encodeURIComponent(id)}`, {
    data: { reasonForChange },
  });
}

// ── Reports ─────────────────────────────────────────────────────────────

export type MatrixCellStatus = 'not_started' | 'in_progress' | 'overdue' | 'completed' | 'expired';
/** Absent cell = not_enrolled; the grid renders that default. */
export type MatrixStatus = MatrixCellStatus | 'not_enrolled';

export interface CompletionMatrix {
  courses: { id: string; slug: string; title: string; category: string | null; version: number }[];
  members: {
    membershipId: string;
    name: string;
    email: string;
    role: SystemRole;
    departmentId: string | null;
  }[];
  cells: {
    courseId: string;
    membershipId: string;
    status: MatrixCellStatus;
    cycleNumber: number;
    dueAt: string | null;
    completedAt: string | null;
    expiresAt: string | null;
  }[];
}

export async function getCompletionMatrix(
  params: { courseId?: string; departmentId?: string } = {},
): Promise<CompletionMatrix> {
  const search = new URLSearchParams();
  if (params.courseId) search.set('courseId', params.courseId);
  if (params.departmentId) search.set('departmentId', params.departmentId);
  const query = search.toString() ? `?${search.toString()}` : '';
  const res = await apiClient.get<{ data: CompletionMatrix }>(
    `/api/training/reports/completion-matrix${query}`,
  );
  return res.data.data;
}

export interface OverdueRow {
  enrollmentId: string;
  course: { id: string; slug: string; title: string };
  membershipId: string;
  traineeName: string;
  traineeEmail: string;
  departmentId: string | null;
  cycleNumber: number;
  dueAt: string | null;
  daysOverdue: number;
  startedAt: string | null;
  overdueAlertedAt: string | null;
}

export async function listOverdue(): Promise<OverdueRow[]> {
  const res = await apiClient.get<{ data: OverdueRow[] }>('/api/training/reports/overdue');
  return res.data.data;
}

// ── Onboarding checklists ───────────────────────────────────────────────

export type ChecklistItemKind = 'course' | 'sop_ack' | 'manual';
export type ChecklistItemStatus = 'pending' | 'complete' | 'waived';

export interface ChecklistTemplateItem {
  id: string;
  templateId: string;
  order: number;
  kind: ChecklistItemKind;
  courseId: string | null;
  sopId: string | null;
  title: string;
  description: string | null;
  dueInDays: number | null;
}

export interface ChecklistTemplate {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  targetRole: string | null;
  targetDepartmentId: string | null;
  isActive: boolean;
  createdByMembershipId: string | null;
  createdAt: string;
  updatedAt: string;
  items: ChecklistTemplateItem[];
  /** Present on list/get payloads only. */
  _count?: { instances: number };
}

export async function listChecklistTemplates(
  opts: { includeInactive?: boolean } = {},
): Promise<ChecklistTemplate[]> {
  const query = opts.includeInactive ? '?includeInactive=true' : '';
  const res = await apiClient.get<{ data: ChecklistTemplate[] }>(
    `/api/training/checklists/templates${query}`,
  );
  return res.data.data;
}

export interface ChecklistTemplateItemInput {
  kind: ChecklistItemKind;
  title: string;
  description?: string | null;
  courseId?: string | null;
  sopId?: string | null;
  dueInDays?: number | null;
}

export interface CreateChecklistTemplateRequest {
  name: string;
  description?: string | null;
  targetRole?: string | null;
  targetDepartmentId?: string | null;
  items: ChecklistTemplateItemInput[];
}

export async function createChecklistTemplate(
  body: CreateChecklistTemplateRequest,
): Promise<ChecklistTemplate> {
  const res = await apiClient.post<{ data: ChecklistTemplate }>('/api/training/checklists/templates', body);
  return res.data.data;
}

export interface UpdateChecklistTemplateRequest {
  name?: string;
  description?: string | null;
  targetRole?: string | null;
  targetDepartmentId?: string | null;
  isActive?: boolean;
  /** When present, REPLACES the item set (order = array order). */
  items?: ChecklistTemplateItemInput[];
  reasonForChange: string;
}

export async function updateChecklistTemplate(
  id: string,
  body: UpdateChecklistTemplateRequest,
): Promise<ChecklistTemplate> {
  const res = await apiClient.put<{ data: ChecklistTemplate }>(
    `/api/training/checklists/templates/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

export async function deactivateChecklistTemplate(id: string, reasonForChange: string): Promise<void> {
  await apiClient.delete(`/api/training/checklists/templates/${encodeURIComponent(id)}`, {
    data: { reasonForChange },
  });
}

export interface ChecklistInstanceItem {
  id: string;
  instanceId: string;
  templateItemId: string | null;
  order: number;
  kind: ChecklistItemKind;
  title: string;
  courseId: string | null;
  sopId: string | null;
  status: ChecklistItemStatus;
  completedAt: string | null;
  completedByMembershipId: string | null;
  notes: string | null;
}

export interface ChecklistInstance {
  id: string;
  organizationId: string;
  templateId: string | null;
  templateNameSnapshot: string;
  employeeId: string;
  createdByMembershipId: string | null;
  createdAt: string;
  completedAt: string | null;
  items: ChecklistInstanceItem[];
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    membershipId: string | null;
    departmentId: string | null;
  };
}

export async function listChecklistInstances(employeeId?: string): Promise<ChecklistInstance[]> {
  const query = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
  const res = await apiClient.get<{ data: ChecklistInstance[] }>(
    `/api/training/checklists/instances${query}`,
  );
  return res.data.data;
}

export async function createChecklistInstance(body: {
  templateId: string;
  employeeId: string;
}): Promise<ChecklistInstance> {
  const res = await apiClient.post<{ data: ChecklistInstance }>('/api/training/checklists/instances', body);
  return res.data.data;
}

export async function setChecklistItemStatus(
  instanceId: string,
  itemId: string,
  body: { status: ChecklistItemStatus; notes?: string | null },
): Promise<{ item: ChecklistInstanceItem; instanceCompletedAt: string | null }> {
  const res = await apiClient.patch<{
    data: { item: ChecklistInstanceItem; instanceCompletedAt: string | null };
  }>(
    `/api/training/checklists/instances/${encodeURIComponent(instanceId)}/items/${encodeURIComponent(itemId)}`,
    body,
  );
  return res.data.data;
}
